/**
 * The admin permission keys the dashboard checks to show pages and buttons.
 *
 * Mirrors `Permission` in okito-backend (`src/features/admin-access/domain/
 * permissions.ts`). The API is the authority — it refuses every call the admin
 * lacks the permission for — so these only decide what is worth showing. The
 * Roles page does not use this list: it renders the API's own catalogue, so a
 * permission added on the backend appears there without a dashboard release.
 */
export enum AdminPermission {
  DashboardView = 'dashboard.view',
  CommandCenterView = 'command_center.view',

  UsersView = 'users.view',
  PartnersView = 'partners.view',
  PartnersManage = 'partners.manage',
  FleetsView = 'fleets.view',
  FleetsManage = 'fleets.manage',

  BookingsView = 'bookings.view',
  TopupsView = 'topups.view',
  TopupsManage = 'topups.manage',
  WithdrawalsView = 'withdrawals.view',
  WithdrawalsManage = 'withdrawals.manage',
  PaymentsView = 'payments.view',
  PaymentsManage = 'payments.manage',
  AffiliatesView = 'affiliates.view',
  AffiliatesManage = 'affiliates.manage',

  CouponsView = 'coupons.view',
  CouponsManage = 'coupons.manage',
  RewardsView = 'rewards.view',
  RewardsManage = 'rewards.manage',

  ServicesManage = 'services.manage',
  AdsManage = 'ads.manage',
  VehiclesManage = 'vehicles.manage',
  SurgePricingView = 'surge_pricing.view',
  SurgePricingManage = 'surge_pricing.manage',
  ServiceFeesView = 'service_fees.view',
  ServiceFeesManage = 'service_fees.manage',
  TnvsFaresView = 'tnvs_fares.view',
  TnvsFaresManage = 'tnvs_fares.manage',
  PaymentMethodsView = 'payment_methods.view',
  PaymentMethodsManage = 'payment_methods.manage',
  PickupPointsView = 'pickup_points.view',
  PickupPointsManage = 'pickup_points.manage',

  NewsManage = 'news.manage',
  NotificationsView = 'notifications.view',
  NotificationsManage = 'notifications.manage',

  ReportsView = 'reports.view',

  AdminsView = 'admins.view',
  AdminsManage = 'admins.manage',
  SystemManage = 'system.manage',
}

/**
 * Landing pages in navigation order. When an admin opens a page they cannot
 * see (most often the Overview, right after login), they are sent to the
 * first of these they can.
 */
export const ADMIN_LANDING_PAGES: { path: string; permission: AdminPermission }[] = [
  { path: '/', permission: AdminPermission.DashboardView },
  { path: '/admin/users', permission: AdminPermission.UsersView },
  { path: '/admin/riders', permission: AdminPermission.PartnersView },
  { path: '/admin/accounts', permission: AdminPermission.AdminsView },
  { path: '/admin/taxi-fleet', permission: AdminPermission.FleetsView },
  { path: '/admin/transactions', permission: AdminPermission.BookingsView },
  { path: '/admin/topups', permission: AdminPermission.TopupsView },
  { path: '/admin/withdrawal-cms', permission: AdminPermission.WithdrawalsView },
  { path: '/admin/coupons-management', permission: AdminPermission.CouponsView },
  { path: '/admin/rewards-cms', permission: AdminPermission.RewardsView },
  { path: '/admin/services-cms', permission: AdminPermission.ServicesManage },
  { path: '/admin/ads-management', permission: AdminPermission.AdsManage },
  { path: '/admin/vehicle-cms', permission: AdminPermission.VehiclesManage },
  { path: '/admin/surge-cms', permission: AdminPermission.SurgePricingView },
  { path: '/admin/service-fee-cms', permission: AdminPermission.ServiceFeesView },
  { path: '/admin/fleet-fee-distribution-cms', permission: AdminPermission.ServiceFeesView },
  { path: '/admin/ride-config-cms', permission: AdminPermission.TnvsFaresView },
  { path: '/admin/payment-methods-cms', permission: AdminPermission.PaymentMethodsView },
  { path: '/admin/newsevents-management', permission: AdminPermission.NewsManage },
  { path: '/admin/notification-management', permission: AdminPermission.NotificationsView },
];
